import type { SyntheticEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import InlineIcon from 'components/utils/InlineIcon'

/**
 * Tooltip triggers inside Typography are forced to `inline-flex`, which breaks
 * InlineIcon's text-baseline alignment. Keep the trigger as `inline` so the icon
 * can sit on the same line metrics as surrounding copy (same approach as
 * cancellation-policy / loyalty InlineIcon usages).
 */
const LikelyDateInfoTooltip = styled(Tooltip)`
  && {
    display: inline;
    vertical-align: middle;
  }
  margin-inline-start: ${rem(4)};
`

const LikelyDateInfoTrigger = styled.span`
  cursor: pointer;
`

interface Props {
  placeholderFlightDateLabel: string
  placeholderFlightDateHasNoAlternatives: boolean
  likelyArrivalDateExplanation?: string
  onInfoClick: (event: SyntheticEvent) => void
}

function PlaceholderFlightDateLabel({
  placeholderFlightDateLabel,
  placeholderFlightDateHasNoAlternatives,
  likelyArrivalDateExplanation,
  onInfoClick,
}: Props) {
  const intl = useIntl()

  const likelyDatesLabel = intl.formatMessage({
    id: 'LM14DEh027qpIhRa_2ow',
    defaultMessage: 'Likely {dates}',
    description: 'Estimated local departure and arrival dates for an unscheduled placeholder flight in the trip planner',
  }, { dates: placeholderFlightDateLabel })

  const dateContent = placeholderFlightDateHasNoAlternatives
    ? placeholderFlightDateLabel
    : (
        <>
          {likelyDatesLabel}
          {likelyArrivalDateExplanation && (
            <LikelyDateInfoTooltip description={likelyArrivalDateExplanation} portaled>
              <LikelyDateInfoTrigger onClick={onInfoClick}>
                <InlineIcon Icon={LineInfoCircleIcon} />
              </LikelyDateInfoTrigger>
            </LikelyDateInfoTooltip>
          )}
        </>
      )

  return (
    <BodyText variant="small" colour="neutral-two" as="span">
      {dateContent}
    </BodyText>
  )
}

export default PlaceholderFlightDateLabel
